import React, { useState, useEffect, useRef } from "react";
import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import "./style/whistleForm.css";
import axios from "axios";
import { apiUrl } from "../config/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

export default function WhistleForm() {
  const { lang } = useParams();
  const { t, i18n } = useTranslation("whistle");

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const [formData, setFormData] = useState({
    informationType: "",
    detailedDescription: "",
    incidentDateTime: getCurrentDateTimeLocal(),
    incidentLocation: "",
    uploadedFile: null,
    secret: null, // true = нууцлах, false = нууцлахгүй
    email: "",
    name: "",
    phone: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 20 * 1024 * 1024;

  // Information types with translation keys
  const informationTypes = [
    {
      key: "discrimination",
      labelKey: "informationTypes.discrimination",
    },
    {
      key: "corruption",
      labelKey: "informationTypes.corruption",
    },
    {
      key: "conflict_interest",
      labelKey: "informationTypes.conflict_interest",
    },
    {
      key: "abuse_power",
      labelKey: "informationTypes.abuse_power",
    },
    {
      key: "misuse_assets",
      labelKey: "informationTypes.misuse_assets",
    },
    {
      key: "reputation_damage",
      labelKey: "informationTypes.reputation_damage",
    },
    {
      key: "other_ethics",
      labelKey: "informationTypes.other_ethics",
    },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.informationType)
      newErrors.informationType = t("errors.informationType");
    if (!formData.detailedDescription.trim())
      newErrors.detailedDescription = t("errors.detailedDescription");
    if (!formData.incidentDateTime)
      newErrors.incidentDateTime = t("errors.incidentDateTime");
    if (!formData.incidentLocation.trim())
      newErrors.incidentLocation = t("errors.incidentLocation");
    if (formData.secret === null) newErrors.secret = t("errors.secret");

    // Extra validation
    if (formData.secret === true && !formData.email.trim()) {
      newErrors.email = t("errors.emailRequired");
    }
    if (formData.secret === false) {
      if (!formData.name.trim()) newErrors.name = t("errors.nameRequired");
      if (!formData.phone.trim()) newErrors.phone = t("errors.phoneRequired");
      if (!formData.email.trim())
        newErrors.email = t("errors.emailRequiredPublic");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setFormData((prev) => ({ ...prev, uploadedFile: null }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      setErrors((prev) => ({ ...prev, uploadedFile: t("errors.fileTooLarge") }));
      return;
    }

    setFormData((prev) => ({ ...prev, uploadedFile: file }));
    if (errors.uploadedFile) {
      setErrors((prev) => ({ ...prev, uploadedFile: "" }));
    }
  };

  const resetForm = () => {
    setFormData({
      informationType: "",
      detailedDescription: "",
      incidentDateTime: getCurrentDateTimeLocal(),
      incidentLocation: "",
      uploadedFile: null,
      secret: null,
      email: "",
      name: "",
      phone: "",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result?.split(",")[1];
        if (!base64) {
          reject(new Error("Invalid file data"));
          return;
        }
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error || new Error("File read failed"));
      reader.readAsDataURL(file);
    });

  const getApiErrorMessage = (data) => {
    if (data?.error) {
      return data.error.data?.message || data.error.message;
    }
    if (data?.result?.error) {
      return data.result.error;
    }
    return null;
  };

  const submitForm = async ({ evidenceFile = null, evidenceFilename = "" } = {}) => {
    setLoading(true);

    try {
      let dateValue = formData.incidentDateTime;
      if (dateValue) {
        dateValue = dateValue.replace("T", " ") + ":00";
      }

      const params = {
        request_type: formData.informationType,
        description: formData.detailedDescription,
        date: dateValue,
        place: formData.incidentLocation,
        keep_secret: formData.secret,
        email: formData.email,
        phone: formData.phone,
        name: formData.name,
      };

      if (evidenceFile) {
        params.evidence_file = evidenceFile;
        params.evidence_filename = evidenceFilename;
      }

      const response = await axios.post(
        apiUrl("/api/whistle/submit"),
        {
          jsonrpc: "2.0",
          method: "call",
          params,
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 120000,
        }
      );

      console.log("Response:", response.data);

      const apiError = getApiErrorMessage(response.data);
      if (apiError) {
        throw new Error(apiError);
      }

      if (!response.data?.result?.success) {
        throw new Error(t("form.submitError"));
      }

      toast.success(t("form.submitSuccess"), {
        position: "bottom-right",
        autoClose: 5000,
        style: { fontSize: "14px" },
      });
      resetForm();
    } catch (err) {
      console.error("Whistle submit error:", err);
      const message =
        err.response?.data?.error?.data?.message ||
        err.response?.data?.error?.message ||
        err.message ||
        t("form.submitError");
      toast.error(`${t("form.submitError")} ${message}`, {
        position: "bottom-right",
        autoClose: 8000,
        style: { fontSize: "14px" },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const uploadedFile = formData.uploadedFile;

    if (uploadedFile) {
      try {
        const evidenceFile = await readFileAsBase64(uploadedFile);
        await submitForm({
          evidenceFile,
          evidenceFilename: uploadedFile.name,
        });
      } catch (err) {
        console.error("File read error:", err);
        toast.error(t("errors.fileReadError"), {
          position: "bottom-right",
          autoClose: 5000,
          style: { fontSize: "14px" },
        });
      }
      return;
    }

    await submitForm();
  };

  return (
    <div className="form-card">
      {/* Information Type */}
      <div className="form-section">
        <h2 className="form-label">
          {t("form.informationType")} <span className="required">*</span>
        </h2>
        <div className="radio-group">
          {informationTypes.map((type) => (
            <label key={type.key} className="radio-option">
              <input
                type="radio"
                name="informationType"
                value={type.key}
                checked={formData.informationType === type.key}
                onChange={(e) =>
                  handleInputChange("informationType", e.target.value)
                }
              />
              <span>{t(type.labelKey)}</span>
            </label>
          ))}
        </div>
        {errors.informationType && (
          <p className="error">{errors.informationType}</p>
        )}
      </div>

      {/* Detailed Description */}
      <div className="form-section">
        <label className="form-label">
          {t("form.detailedDescription")} <span className="required">*</span>
        </label>
        <textarea
          value={formData.detailedDescription}
          onChange={(e) =>
            handleInputChange("detailedDescription", e.target.value)
          }
          rows={6}
          className={`form-textarea ${
            errors.detailedDescription ? "error-border" : ""
          }`}
        />
        {errors.detailedDescription && (
          <p className="error">{errors.detailedDescription}</p>
        )}
      </div>

      {/* Date Time */}
      <div className="form-section">
        <label className="form-label">
          {t("form.incidentDateTime")} <span className="required">*</span>
        </label>
        <div className="input-icon-wrapper">
          <input
            type="datetime-local"
            value={formData.incidentDateTime}
            onChange={(e) =>
              handleInputChange("incidentDateTime", e.target.value)
            }
            className={`form-input ${
              errors.incidentDateTime ? "error-border" : ""
            }`}
          />
        </div>
        {errors.incidentDateTime && (
          <p className="error">{errors.incidentDateTime}</p>
        )}
      </div>

      {/* Location */}
      <div className="form-section">
        <label className="form-label">
          {t("form.incidentLocation")} <span className="required">*</span>
        </label>
        <input
          type="text"
          value={formData.incidentLocation}
          onChange={(e) =>
            handleInputChange("incidentLocation", e.target.value)
          }
          className={`form-input ${
            errors.incidentLocation ? "error-border" : ""
          }`}
        />
        {errors.incidentLocation && (
          <p className="error">{errors.incidentLocation}</p>
        )}
      </div>

      {/* Evidence Upload */}
      <div className="form-section">
        <div
          className={`file-upload ${
            errors.uploadedFile ? "error-border" : ""
          }`}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <input
            ref={fileInputRef}
            type="file"
            name="evidence"
            id="evidenceUpload"
            onChange={handleFileUpload}
          />
          <Upload className="file-icon" />
          <div className="file-upload-text">
            {formData.uploadedFile
              ? formData.uploadedFile.name
              : t("form.fileUpload")}
            <p>{t("form.maxUploadSize")}</p>
          </div>
        </div>
        {errors.uploadedFile && (
          <p className="error">{errors.uploadedFile}</p>
        )}
        <div className="warningtext">{t("form.fileWarning")}</div>
      </div>

      {/* Secret */}
      <div className="form-section">
        <label className="form-label">
          {t("form.secret")} <span className="required">*</span>
        </label>
        <div className="secret">
          <label>
            <input
              type="radio"
              name="secret"
              value="yes"
              checked={formData.secret === true}
              onChange={() => handleInputChange("secret", true)}
            />
            {t("form.secretYes")}
          </label>
          <label>
            <input
              type="radio"
              name="secret"
              value="no"
              checked={formData.secret === false}
              onChange={() => handleInputChange("secret", false)}
            />
            {t("form.secretNo")}
          </label>
        </div>
        {errors.secret && <p className="error">{errors.secret}</p>}
      </div>

      {/* Conditional fields based on secret choice */}
      {formData.secret === true && (
        <div className="form-section">
          <label className="form-label">
            {t("form.email")} <span className="required">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            className={`form-input ${errors.email ? "error-border" : ""}`}
            placeholder={t("form.emailPlaceholder")}
          />
          {errors.email && <p className="error">{errors.email}</p>}
        </div>
      )}

      {formData.secret === false && (
        <div className="form-section">
          <label className="form-label">
            {t("form.name")} <span className="required">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            className={`form-input ${errors.name ? "error-border" : ""}`}
          />
          {errors.name && <p className="error">{errors.name}</p>}

          <label className="form-label">
            {t("form.phone")} <span className="required">*</span>
          </label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            className={`form-input ${errors.phone ? "error-border" : ""}`}
          />
          {errors.phone && <p className="error">{errors.phone}</p>}

          <label className="form-label">
            {t("form.email")} <span className="required">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            className={`form-input ${errors.email ? "error-border" : ""}`}
          />
          {errors.email && <p className="error">{errors.email}</p>}
        </div>
      )}

      <div className="form-footer">
        <button
          onClick={handleSubmit}
          className="submit-btn"
          disabled={loading}
        >
          {loading ? "..." : t("form.submit")}
        </button>
      </div>
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}
